import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CarouselModal } from "@/components/CarouselModal";
import {
  Sparkles, Loader2, Wand2, ImageIcon, RefreshCw, Trash2, Link2,
  CalendarCheck, Rocket, Lock, Linkedin, Facebook, Check, Share2, CheckCircle2, Eye, Clock, ChevronDown, ChevronUp,
} from "lucide-react";
import PublishPreviewModal from "@/components/PublishPreviewModal";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "facebook", label: "Facebook", icon: Facebook },
];
const PLAT_ICON = { linkedin: Linkedin, facebook: Facebook };
const DURATIONS = [30, 60, 90];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (e) { return ""; }
};
const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch (e) { return ""; }
};

const PILLAR_STYLE = {
  "Educational / Problem-Solving": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Feature & Social Proof": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Engagement / Thought Leadership": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Direct Conversion / Offer": "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
};

const ARCH_BADGE = {
  spec_breakdown: { label: "Spec Breakdown", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  founder_log: { label: "Founder Log", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  hot_take: { label: "Hot Take", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  carousel: { label: "PDF Carousel", cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30" },
};
const ASPECT = { spec: "aspect-square", log: "aspect-square", quote: "aspect-square", carousel: "aspect-[4/5]", photo: "aspect-square" };
const NEXT_STYLE = { spec: "quote", quote: "log", log: "card", card: "spec", photo: "spec" };
const PHASES = [
  { key: "scanning", label: "Scanning Website..." },
  { key: "extracting", label: "Extracting Brand DNA Specs..." },
  { key: "building", label: "Building Campaign..." },
];

const STYLE_OPTIONS = [
  { value: "spec", label: "Terminal spec card" },
  { value: "quote", label: "High-contrast quote" },
  { value: "log", label: "Founder log card" },
  { value: "photo", label: "AI photo · auto product" },
  { value: "card", label: "Branded photo card" },
  { value: "photorealistic", label: "AI · Photorealistic" },
  { value: "3d", label: "AI · 3D Render" },
  { value: "bold", label: "AI · Bold Graphic" },
  { value: "minimalist", label: "AI · Minimalist" },
  { value: "illustration", label: "AI · Illustration" },
  { value: "retro", label: "AI · Retro" },
  { value: "product", label: "AI · Product Photo" },
];
const STYLE_LABEL = Object.fromEntries(STYLE_OPTIONS.map((o) => [o.value, o.label]));

function CampaignCard({ idx, card, cid, onChange, onDelete, products = [], preview = false }) {
  const [copy, setCopy] = useState(card.post_text);
  const [busyImg, setBusyImg] = useState(!card.image_path);
  const [busyCopy, setBusyCopy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [imgStyle, setImgStyle] = useState(card.image_style || (card.visual_style !== "carousel" && card.visual_style) || "card");
  const [showCarousel, setShowCarousel] = useState(false);
  const [switching, setSwitching] = useState(false);
  const isCarousel = card.visual_style === "carousel";
  const badge = ARCH_BADGE[card.archetype];
  const [productId, setProductId] = useState(card.product_id || "");
  const PIcon = PLAT_ICON[card.platform] || Facebook;
  const imgUrl = card.image_path ? `${API}/media/${card.image_path}` : null;

  // Sync spinner with prop updates: when the image finishes rendering (patched via props),
  // stop the spinner even though this card mounted before its graphic was ready.
  useEffect(() => {
    if (card.image_path) setBusyImg(false);
  }, [card.image_path]);

  const saveCopy = async () => {
    if (copy.trim() === card.post_text) return;
    try {
      await api.patch(`/campaign/${cid}/card/${card.id}`, { post_text: copy });
      onChange({ ...card, post_text: copy });
    } catch (e) { toast.error("Could not save edit"); }
  };
  const switchStyle = async () => {
    const cur = card.visual_style && card.visual_style !== "carousel" ? card.visual_style : "card";
    const next = NEXT_STYLE[cur] || "spec";
    setSwitching(true); setBusyImg(true);
    try {
      const r = await api.post(`/campaign/${cid}/card/${card.id}/image`, { style: next });
      setImgStyle(next);
      onChange({ ...card, image_path: r.data.image_path, image_style: next, visual_style: next });
    } catch (e) { toast.error("Could not switch the card style"); }
    finally { setSwitching(false); setBusyImg(false); }
  };
  const regenImage = async () => {
    setBusyImg(true);
    try {
      const body = isCarousel ? {} : imgStyle === "card"
        ? (card.visual_style && card.visual_style !== "card" ? { style: "card" } : {})
        : { style: imgStyle, ...(imgStyle === "product" && productId ? { product_id: productId } : {}) };
      const r = await api.post(`/campaign/${cid}/card/${card.id}/image`, body);
      const vs = ["spec", "quote", "log", "card", "photo"].includes(imgStyle) && !isCarousel ? imgStyle : card.visual_style;
      onChange({ ...card, ...r.data, image_path: r.data.image_path, image_style: imgStyle, visual_style: vs, product_id: productId || card.product_id });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "object" ? d?.message : d) || (imgStyle === "card" ? "Image generation failed" : "AI image failed — try again"));
    }
    finally { setBusyImg(false); }
  };
  const swapCopy = async () => {
    setBusyCopy(true);
    try {
      const r = await api.post(`/campaign/${cid}/card/${card.id}/swap-copy`);
      setCopy(r.data.post_text);
      onChange({ ...card, post_text: r.data.post_text, hashtags: r.data.hashtags });
    } catch (e) { toast.error("Copy rewrite failed"); }
    finally { setBusyCopy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`campaign-card-${idx}`}>
      <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-border">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border ${PILLAR_STYLE[card.pillar] || "bg-secondary text-muted-foreground border-border"}`}>
            Day {String(card.day).padStart(2, "0")}
          </span>
          {badge && <span className={`text-[10px] font-semibold px-2 py-1 rounded-md border truncate ${badge.cls}`} data-testid={`card-archetype-badge-${idx}`}>{badge.label}</span>}
          {card.visual_style === "photo" && <span className="text-[10px] font-semibold px-2 py-1 rounded-md border border-rose-500/30 bg-rose-500/15 text-rose-300 shrink-0" data-testid={`card-photo-badge-${idx}`}>{card.photo_source === "product" ? "Product photo" : "AI photo"}</span>}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <PIcon className="h-3.5 w-3.5" /> {card.platform}
        </span>
      </div>

      <div className={`relative ${ASPECT[card.visual_style] || "aspect-[1200/630]"} bg-secondary/40 shrink-0`}>
        {busyImg ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : imgUrl ? (
          <img src={imgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`card-image-${idx}`} />
        ) : null}
        {!busyImg && imgUrl && !isCarousel && (
          <button onClick={switchStyle} disabled={switching} data-testid={`card-switch-style-${idx}`}
            className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-white hover:bg-black/85 transition-colors">
            <RefreshCw className="h-3 w-3" /> Switch Card Style
          </button>
        )}
        {!busyImg && imgUrl && isCarousel && (
          <button onClick={() => setShowCarousel(true)} data-testid={`card-open-carousel-${idx}`}
            className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-[#0A66C2] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#0A66C2]/90 transition-colors">
            <Eye className="h-3 w-3" /> {card.slide_paths?.length || 5} slides · PDF
          </button>
        )}
        {busyImg || imgUrl ? null : (
          <button onClick={regenImage} className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:text-primary transition-colors">
            <ImageIcon className="h-6 w-6" /><span className="text-xs">Generate graphic</span>
          </button>
        )}
      </div>

      <div className="p-3.5 flex flex-col gap-2 flex-1">
        <Textarea
          data-testid={`card-copy-${idx}`}
          value={copy}
          onChange={(e) => setCopy(e.target.value)}
          onBlur={saveCopy}
          className="min-h-[104px] text-sm resize-none bg-secondary/30 border-border"
        />
        {card.anchor && (
          <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`card-anchor-${idx}`}>
            <span className="font-mono uppercase tracking-wider text-emerald-400/90">Anchored on</span> · {card.anchor}
          </p>
        )}
        {card.asset_prompt && (
          <details className="text-[11px] text-muted-foreground" data-testid={`card-blueprint-${idx}`}>
            <summary className="cursor-pointer font-mono uppercase tracking-wider hover:text-foreground">Visual blueprint</summary>
            <p className="whitespace-pre-line mt-1 leading-snug">{card.asset_prompt}</p>
          </details>
        )}
        {card.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {card.hashtags.map((h) => <span key={h} className="text-[11px] text-primary/80">{h}</span>)}
          </div>
        )}
        {isCarousel && (
          <Button variant="outline" size="sm" onClick={() => setShowCarousel(true)} data-testid={`card-carousel-btn-${idx}`}
            className="h-8 text-xs border-[#0A66C2]/50 text-[#5aa3ea] hover:bg-[#0A66C2]/10">
            <Linkedin className="h-3.5 w-3.5 mr-1.5" /> Export / Queue as LinkedIn Carousel PDF
          </Button>
        )}
        {!preview && !isCarousel && <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Visual</span>
          <select
            data-testid={`card-image-style-${idx}`}
            value={imgStyle}
            onChange={(e) => setImgStyle(e.target.value)}
            className="flex-1 min-w-0 h-7 rounded-md bg-secondary/40 border border-border text-xs px-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {STYLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>}
        {imgStyle === "product" && products.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Product</span>
            <select
              data-testid={`card-product-select-${idx}`}
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="flex-1 min-w-0 h-7 rounded-md bg-secondary/40 border border-border text-xs px-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">First in stock</option>
              {products.map((p) => (
                <option key={p.shopify_id} value={p.shopify_id}>
                  {p.title}{p.in_stock === false ? " (out of stock)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-auto pt-1">
          <Button variant="outline" size="sm" onClick={regenImage} disabled={busyImg} data-testid={`card-regen-image-${idx}`} className="flex-1 min-w-0 h-8 text-xs border-border">
            {busyImg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (["card", "spec", "quote", "log"].includes(imgStyle) || isCarousel ? <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /> : <Sparkles className="h-3.5 w-3.5 sm:mr-1" />)}
            <span className="hidden sm:inline">{["card", "spec", "quote", "log"].includes(imgStyle) || isCarousel ? "Image" : "AI image"}</span>
          </Button>
          {!preview && <Button variant="outline" size="sm" onClick={swapCopy} disabled={busyCopy} data-testid={`card-swap-copy-${idx}`} className="flex-1 min-w-0 h-8 text-xs border-border">
            {busyCopy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 sm:mr-1" />}
            <span className="hidden sm:inline">Swap</span>
          </Button>}
          <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} data-testid={`card-preview-${idx}`} className="flex-1 min-w-0 h-8 text-xs border-border">
            <Eye className="h-3.5 w-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Preview</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(card.id)} data-testid={`card-delete-${idx}`} className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-400 shrink-0">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {isCarousel && (
        <CarouselModal open={showCarousel} onOpenChange={setShowCarousel} cid={cid} card={card} onChange={onChange} preview={preview} />
      )}
      <PublishPreviewModal
        open={showPreview}
        onOpenChange={setShowPreview}
        post={{ platform: card.platform, post_text: copy, hashtags: card.hashtags, image_path: card.image_path }}
      />
    </div>
  );
}

export default function CampaignGenerator() {
  const { active } = useWorkspace();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const isFree = !user?.is_admin && !["creator", "growth", "agency", "pro"].includes(user?.plan) && !(user?.campaign_credits > 0);
  const inTrial = !!user?.in_paid_trial && !user?.is_admin;
  const maxDays = user?.is_admin ? 90 : inTrial ? 30 : (user?.limits?.campaign_days || 30);
  const [days, setDays] = useState(30);
  const [url, setUrl] = useState("");
  const [platforms, setPlatforms] = useState(["linkedin", "facebook"]);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [approving, setApproving] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [cards, setCards] = useState([]);
  const [scheduled, setScheduled] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [sharedOnce, setSharedOnce] = useState(false);
  const [clientApproved, setClientApproved] = useState(false);
  const [bulkStyle, setBulkStyle] = useState("photorealistic");
  const [stylingAll, setStylingAll] = useState(false);
  const [styledDone, setStyledDone] = useState(0);
  const [products, setProducts] = useState([]);
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [phase, setPhase] = useState(null);
  const [dna, setDna] = useState(null);
  const autoRanRef = useRef(false);

  // Scan-to-Signup: if the visitor scanned a URL on the funnel/landing before signing up,
  // carry it straight into the generator and auto-build — no re-entry.
  useEffect(() => {
    if (!active || autoRanRef.current) return;
    let saved = "";
    try { saved = localStorage.getItem("ags_scan_url") || ""; } catch (e) {}
    if (saved && saved.length >= 3) {
      autoRanRef.current = true;
      try { localStorage.removeItem("ags_scan_url"); } catch (e) {}
      setUrl(saved);
      toast.message("Picking up where you left off", { description: `Building your campaign from ${saved}` });
      setTimeout(() => generateFromUrl(saved), 150);
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;
    api.get(`/shopify/products?workspace_id=${active.id}`)
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, [active]);

  // Poll the shared campaign so the agency sees "client approved" without a manual refresh.
  useEffect(() => {
    if (!campaign || !sharedOnce || clientApproved || scheduled > 0) return;
    const check = async () => {
      try {
        const r = await api.get(`/campaign/${campaign.id}`);
        if (r.data?.client_approved) {
          setClientApproved(true);
          toast.success("Your client approved this campaign!");
        }
      } catch (e) {}
    };
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
  }, [campaign, sharedOnce, clientApproved, scheduled]);

  const shareWithClient = async () => {
    if (!campaign) return;
    setSharing(true);
    try {
      const r = await api.post(`/campaign/${campaign.id}/share`);
      const url = r.data.url?.startsWith("http") ? r.data.url : `${window.location.origin}/portfolio/${r.data.share_token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      setSharedOnce(true);
      if (r.data.client_approved) setClientApproved(true);
      toast.success("Client approval link copied to clipboard", { description: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create a share link");
    } finally { setSharing(false); }
  };
  const cancelRef = useRef(false);
  const dispatchedRef = useRef(new Set());

  const togglePlatform = (id) =>
    setPlatforms((p) => (p.includes(id) ? (p.length > 1 ? p.filter((x) => x !== id) : p) : [...p, id]));

  // batch-render images in chunks (one browser session per chunk) so the grid
  // populates progressively AND fast (vs a browser launch per card)
  const loadImages = useCallback(async (cid, cardList) => {
    const ids = cardList.filter((c) => !c.image_path && !c.skeleton).map((c) => c.id);
    const chunks = [];
    for (let i = 0; i < ids.length; i += 6) chunks.push(ids.slice(i, i + 6));
    const worker = async () => {
      while (chunks.length && !cancelRef.current) {
        const group = chunks.shift();
        try {
          const r = await api.post(`/campaign/${cid}/render-all`, { card_ids: group });
          const paths = r.data.paths || {};
          setCards((prev) => prev.map((c) => (paths[c.id] ? { ...c, image_path: paths[c.id] } : c)));
        } catch (e) { /* leave as generate-on-demand */ }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
  }, []);

  const pollCampaign = async (cid) => {
    // Stream cards: poll the campaign and append posts as each AI chunk lands, rendering their
    // graphics as they arrive so the feed fills progressively instead of after one long wait.
    while (!cancelRef.current) {
      await new Promise((res) => setTimeout(res, 1200));
      if (cancelRef.current) return;
      let data;
      try { data = (await api.get(`/campaign/${cid}`)).data; } catch (e) { continue; }
      const real = data.cards || [];
      if (data.phase) setPhase(data.phase);
      if (data.brand_dna) setDna(data.brand_dna);
      if (real.length) setLoading(false);
      const exp = data.expected_cards || 30;
      const fillers = Math.max(0, exp - real.length);
      setCards([...real, ...Array.from({ length: fillers }, (_, i) => ({ id: `skeleton-${i}`, skeleton: true }))]);
      if (data.status !== "generating") {
        setLoading(false); setStreaming(false);
        setCampaign((c) => ({ ...(c || {}), status: data.status }));
        // Final safety: render any card the backend stream didn't manage to render.
        const remaining = real.filter((c) => !c.image_path);
        if (remaining.length) loadImages(cid, remaining);
        break;
      }
    }
  };

  // Reopen a campaign: ?id=<cid> or the last campaign built in this workspace.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (!active || resumedRef.current || autoRanRef.current) return;
    resumedRef.current = true;
    let cid = new URLSearchParams(window.location.search).get("id");
    try { cid = cid || localStorage.getItem(`ags_last_campaign_${active.id}`); } catch (e) {}
    if (!cid) return;
    api.get(`/campaign/${cid}`).then((r) => {
      const d = r.data;
      if (!d || d.workspace_id !== active.id) return;
      setCampaign(d);
      setUrl(d.source_url || "");
      setDna(d.brand_dna || null);
      setPhase(d.phase || "done");
      setCards(d.cards || []);
      if (d.status === "scheduled") setScheduled((d.cards || []).length);
      if (d.status === "generating") { setStreaming(true); pollCampaign(cid); }
    }).catch(() => { try { localStorage.removeItem(`ags_last_campaign_${active.id}`); } catch (e) {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const generateFromUrl = async (targetUrl) => {
    if (!active) return;
    const u = (targetUrl || "").trim();
    if (!u) { toast.error("Paste a website or landing page URL first"); return; }
    cancelRef.current = false;
    dispatchedRef.current = new Set();
    setLoading(true);
    setStreaming(true);
    setScheduled(0);
    setCards(Array.from({ length: isFree ? 3 : days }, (_, i) => ({ id: `skeleton-${i}`, skeleton: true })));
    setCampaign(null);
    setPhase("scanning");
    setDna(null);
    let cid = null;
    try {
      const r = await api.post("/campaign/generate", { workspace_id: active.id, url: u, platforms, days });
      cid = r.data.id;
      try { localStorage.setItem(`ags_last_campaign_${active.id}`, cid); } catch (e) {}
      setCampaign(r.data);
      setPhase(r.data.phase || "extracting");
      if (checkAuth) checkAuth();
    } catch (e) {
      setLoading(false);
      setStreaming(false);
      setPhase(null);
      setCards([]);
      const msg = e?.response?.data?.detail;
      if (msg && typeof msg === "object" && ["preview_used", "campaign_days", "trial_campaign_used", "trial_days"].includes(msg.code)) {
        toast.error(msg.message, { duration: 8000, action: { label: "See plans", onClick: () => navigate("/billing") } });
      } else if (msg && typeof msg === "object" && msg.code === "storage_limit") {
        toast.error(msg.message, { duration: 8000 });
      } else {
        toast.error(typeof msg === "string" ? msg : "Generation failed. Try another URL.");
      }
      return;
    }
    await pollCampaign(cid);
  };

  const generate = () => generateFromUrl(url);

  const updateCard = (updated) => setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  // Schedule preview: show exactly when each post will publish (peak-time engine) before scheduling.
  useEffect(() => {
    if (!campaign || scheduled > 0 || loading || streaming) { setSchedulePreview(null); return; }
    const realCount = cards.filter((c) => !c.skeleton).length;
    if (realCount === 0) return;
    let cancelled = false;
    api.get(`/campaign/${campaign.id}/schedule-preview`)
      .then((r) => { if (!cancelled) setSchedulePreview(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, scheduled, loading, streaming, cards.length, cards.filter((c) => c.queued_post_id).length]);

  const applyStyleAll = async () => {
    if (!campaign || stylingAll) return;
    const ids = cards.filter((c) => !c.skeleton && c.visual_style !== "carousel").map((c) => c.id);
    if (!ids.length) return;
    setStylingAll(true);
    setStyledDone(0);
    const queue = [...ids];
    const body = bulkStyle === "card" ? {} : { style: bulkStyle };
    let stop = false;
    const worker = async () => {
      while (queue.length && !stop) {
        const id = queue.shift();
        try {
          const r = await api.post(`/campaign/${campaign.id}/card/${id}/image`, body);
          setCards((prev) => prev.map((c) => (c.id === id ? { ...c, image_path: r.data.image_path, image_style: bulkStyle, ...(["spec", "quote", "log", "card", "photo"].includes(bulkStyle) ? { visual_style: bulkStyle } : {}) } : c)));
        } catch (e) {
          const d = e?.response?.data?.detail;
          if (d && typeof d === "object" && d.code === "no_product") { stop = true; toast.error(d.message); break; }
        }
        setStyledDone((n) => n + 1);
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setStylingAll(false);
    if (!stop) toast.success(`Applied ${STYLE_LABEL[bulkStyle] || bulkStyle} to your whole campaign`);
  };

  const deleteCard = async (id) => {
    setCards((prev) => prev.filter((c) => c.id !== id));
    try { await api.delete(`/campaign/${campaign.id}/card/${id}`); } catch (e) {}
  };

  const approve = async () => {
    if (!campaign) return;
    setApproving(true);
    try {
      const r = await api.post(`/campaign/${campaign.id}/approve`, { hour: 9 });
      setScheduled(r.data.scheduled);
      if (r.data.first_campaign) setCelebrate(r.data.scheduled);
      else if (r.data.webhook?.ok) toast.success(`${r.data.scheduled} posts scheduled & sent to your publishing webhook`);
      else toast.success(`${r.data.scheduled} posts queued to your calendar`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not schedule the campaign"));
    } finally { setApproving(false); }
  };

  const realCards = cards.filter((c) => !c.skeleton);
  const queuedSolo = realCards.filter((c) => c.queued_post_id).length;
  const toSchedule = realCards.length - queuedSolo;
  const readyGraphics = realCards.filter((c) => c.image_path).length;
  const isPreview = !!campaign?.is_preview;
  const canApprove = campaign && !isPreview && realCards.length > 0 && scheduled === 0;
  const total = cards.length || (isFree ? 3 : days);

  if (!active) return null;

  return (
    <div className="pb-28" data-testid="campaign-page">
      {/* Top control bar */}
      <div className="sticky top-16 lg:top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center"><Sparkles className="h-5 w-5 text-primary" /></div>
          <div>
            <h1 className="text-xl font-display font-bold tracking-tight leading-tight" data-testid="campaign-title">{isFree ? "1-Click Campaign · Free Preview" : `1-Click ${days}-Day Campaign`}</h1>
            <p className="text-xs text-muted-foreground">Paste your website link — we extract your brand voice & offer, then build a full month of on-brand posts + graphics.</p>
          </div>
        </div>
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5" data-testid="campaign-url-label">Your Business Website</label>
        <div className="flex flex-col lg:flex-row gap-2.5">
          <div className="relative flex-1 min-w-0">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="campaign-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && generate()}
              placeholder="Paste your website link to extract your brand voice & offer..."
              className="pl-9 h-11 bg-secondary/40"
            />
          </div>
          <Button data-testid="campaign-generate-btn" onClick={generate} disabled={loading}
            className="h-11 bg-primary hover:bg-primary/90 text-white font-medium px-5 shrink-0">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Building your month…</>
              : <><Sparkles className="h-4 w-4 mr-2" /> Analyze Brand DNA →</>}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-muted-foreground mr-1">Publish to</span>
          {PLATFORMS.map((p) => {
            const on = platforms.includes(p.id);
            return (
              <button key={p.id} data-testid={`platform-toggle-${p.id}`} onClick={() => togglePlatform(p.id)}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-primary/50 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <p.icon className="h-3.5 w-3.5" /> {p.label}
                {on && <Check className="h-3 w-3" />}
              </button>
            );
          })}
          <span className="text-xs text-muted-foreground ml-3 mr-1">Length</span>
          {DURATIONS.map((d) => {
            const locked = isFree || d > maxDays;
            const on = !isFree && days === d;
            return (
              <button key={d} data-testid={`campaign-days-${d}`} disabled={loading}
                onClick={() => (locked ? toast.message(isFree ? "Free accounts get a 3-post preview" : `${d}-day campaigns are on a higher tier`, { description: "Upgrade to unlock longer campaigns & scheduling.", action: { label: "See plans", onClick: () => navigate("/billing") } }) : setDays(d))}
                className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-primary/50 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"} ${locked ? "opacity-60" : ""}`}>
                {locked && <Lock className="h-3 w-3" />} {d} days
              </button>
            );
          })}
          {isFree && <span className="text-[11px] text-amber-400 ml-1" data-testid="campaign-free-note">Free preview: 3 posts with graphics</span>}
          {inTrial && <span className="text-[11px] text-emerald-400 ml-1" data-testid="campaign-trial-note">$1 trial: {user?.trial_campaigns_left ? "your full 30-day campaign is included" : "trial campaign used — more unlock on day 7"}</span>}
        </div>
      </div>

      {/* Streaming progress — subtle "building X/30" feedback while posts generate */}
      {streaming && (
        <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="streaming-progress">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="flex items-center gap-3 font-medium text-foreground flex-wrap" data-testid="campaign-phase-steps">
              {PHASES.map((ph, k) => {
                const at = PHASES.findIndex((x) => x.key === (phase === "done" ? "building" : phase));
                const state = k < at ? "done" : k === at ? "now" : "todo";
                const label = ph.key === "building" ? `Building ${isFree ? "3-Post Preview" : `${days}-Day Campaign`}...${state === "now" && realCards.length ? ` ${realCards.length}/${total}` : ""}` : ph.label;
                return (
                  <span key={ph.key} data-testid={`campaign-phase-${ph.key}`} data-state={state}
                    className={`flex items-center gap-1.5 ${state === "todo" ? "text-muted-foreground/60" : state === "done" ? "text-emerald-400" : "text-foreground"}`}>
                    {state === "done" ? <Check className="h-3.5 w-3.5" /> : state === "now" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                    {label}
                  </span>
                );
              })}
            </span>
            <span className="text-muted-foreground" data-testid="streaming-progress-count">{readyGraphics}/{total} graphics</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary/60 overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${Math.max(phase === "scanning" ? 4 : phase === "extracting" ? 10 : 15, Math.round((realCards.length / total) * 100))}%` }} data-testid="streaming-progress-bar" />
          </div>
        </div>
      )}

      {dna && (
        <details className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 group" open={streaming} data-testid="brand-dna-panel">
          <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2 list-none">
            <Sparkles className="h-4 w-4 text-emerald-400" /> Brand DNA extracted
            <span className="text-xs font-normal text-muted-foreground">· {(dna.product_specs || []).length} specs · {(dna.verifiable_metrics || []).length} verified metrics{dna.target_buyer ? ` · for ${dna.target_buyer}` : ""}</span>
          </summary>
          <div className="grid md:grid-cols-3 gap-4 mt-3 text-xs">
            {[["Product specs", dna.product_specs, "dna-specs"], ["Verifiable metrics", dna.verifiable_metrics, "dna-metrics"], ["Pain points", dna.pain_points, "dna-pains"]].map(([t, list, tid]) => (
              <div key={tid} data-testid={tid}>
                <p className="font-mono uppercase tracking-wider text-muted-foreground mb-1.5">{t}</p>
                <ul className="space-y-1">{(list || []).map((x, k) => <li key={k} className="text-foreground/90 leading-snug">· {x}</li>)}</ul>
              </div>
            ))}
          </div>
          {dna.brand_voice_tone && <p className="text-xs text-muted-foreground mt-3" data-testid="dna-voice">Voice: <span className="text-foreground/90">{dna.brand_voice_tone}</span>{dna.pitch_style && <> · Pitch: <span className="text-foreground/90" data-testid="dna-pitch">{dna.pitch_style}</span></>}</p>}
        </details>
      )}

      {/* Grid */}
      <div className="mt-6">
        {campaign && !isPreview && realCards.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-4 rounded-xl border border-border bg-card/60 px-4 py-3" data-testid="style-all-bar">
            <ImageIcon className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium mr-1">Apply a visual style to all {realCards.length} posts</span>
            <select
              data-testid="bulk-style-select"
              value={bulkStyle}
              onChange={(e) => setBulkStyle(e.target.value)}
              disabled={stylingAll}
              className="h-8 rounded-md bg-secondary/40 border border-border text-xs px-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {STYLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Button
              data-testid="apply-style-all-btn"
              size="sm"
              onClick={applyStyleAll}
              disabled={stylingAll}
              className="h-8 bg-primary hover:bg-primary/90 text-white"
            >
              {stylingAll
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Styling {styledDone}/{realCards.length}…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Apply to all</>}
            </Button>
            {bulkStyle === "product" && <span className="text-[11px] text-muted-foreground">Uses your synced Shopify product photo</span>}
          </div>
        )}
        {cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-24 text-muted-foreground">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <CalendarCheck className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1.5" data-testid="campaign-empty-title">{isFree ? "Your free 3-post preview appears here" : `Your ${days}-day calendar appears here`}</h3>
            <p className="text-sm max-w-sm">{isFree
              ? "Paste your URL above. In seconds you'll see 3 on-brand posts with graphics — upgrade anytime to unlock all 30 and auto-schedule them."
              : `Paste any URL above and hit Analyze. In seconds you'll get ${days} editable posts across educational, social-proof, engagement and conversion angles — each with its own graphic.`}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="campaign-grid">
            {cards.map((c, i) =>
              c.skeleton ? (
                <div key={c.id} className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
                  <div className="h-11 border-b border-border bg-secondary/30" />
                  <div className="aspect-[1200/630] bg-secondary/40" />
                  <div className="p-3.5 space-y-2"><div className="h-3 bg-secondary/50 rounded w-full" /><div className="h-3 bg-secondary/50 rounded w-4/5" /><div className="h-3 bg-secondary/50 rounded w-2/3" /></div>
                </div>
              ) : (
                <CampaignCard key={c.id} idx={i} card={c} cid={campaign.id} onChange={updateCard} onDelete={deleteCard} products={products} preview={isPreview} />
              )
            )}
            {isPreview && !streaming && (
              <div className="rounded-xl border border-dashed border-amber-500/50 bg-amber-500/5 flex flex-col items-center justify-center text-center p-8 min-h-[260px]" data-testid="campaign-preview-locked">
                <Lock className="h-8 w-8 text-amber-400 mb-3" />
                <p className="font-semibold mb-1">{Math.max(0, 30 - realCards.length)} more posts are ready to build</p>
                <p className="text-xs text-muted-foreground max-w-xs mb-4">Upgrade to unlock the full 30-day campaign, auto-scheduled at Facebook &amp; LinkedIn peak times.</p>
                <Button size="sm" onClick={() => navigate("/billing")} data-testid="campaign-preview-upgrade-btn" className="bg-amber-500 hover:bg-amber-500/90 text-white">
                  <Rocket className="h-3.5 w-3.5 mr-1.5" /> Upgrade to unlock all 30 &amp; schedule
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating action bar */}
      {campaign && realCards.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-[70] border-t border-border bg-card/90 backdrop-blur-xl px-4 sm:px-6 lg:px-8 py-3">
          {clientApproved && scheduled === 0 && (
            <div className="max-w-7xl mx-auto mb-2.5 flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2" data-testid="campaign-client-approved-banner">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <p className="text-xs sm:text-sm text-emerald-300 flex-1 min-w-0">Your client approved this campaign — schedule the whole month in one tap.</p>
            </div>
          )}
          {/* Schedule preview — when & where each post publishes, before scheduling */}
          {!isPreview && scheduled === 0 && schedulePreview && schedulePreview.count > 0 && (
            <div className="max-w-7xl mx-auto mb-2.5" data-testid="campaign-schedule-preview">
              <button onClick={() => setPreviewOpen((o) => !o)} data-testid="schedule-preview-toggle"
                className="w-full flex items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-left hover:border-primary/40 transition-colors">
                <Clock className="h-4 w-4 text-primary shrink-0" />
                <span className="text-xs sm:text-sm text-foreground/90 flex-1 min-w-0 truncate">
                  Auto-scheduled at Facebook &amp; LinkedIn peak times · <span className="font-semibold">{fmtDate(schedulePreview.start)} → {fmtDate(schedulePreview.end)}</span> <span className="text-muted-foreground">({schedulePreview.days} days)</span>
                </span>
                {previewOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
              </button>
              {previewOpen && (
                <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-border bg-background/40 divide-y divide-border" data-testid="schedule-preview-list">
                  {schedulePreview.items.map((it, i) => {
                    const Icon = PLAT_ICON[it.platform] || Facebook;
                    return (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`schedule-preview-item-${i}`}>
                        <Icon className={`h-3.5 w-3.5 shrink-0 ${it.platform === "linkedin" ? "text-sky-400" : "text-blue-400"}`} />
                        <span className="w-36 shrink-0 font-mono text-muted-foreground">{fmtDateTime(it.scheduled_at)}</span>
                        <span className="flex-1 min-w-0 truncate text-foreground/80">{it.headline}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold" data-testid="campaign-ready-count">
                {scheduled > 0 ? `${scheduled} posts scheduled` : isPreview ? `Free preview · ${realCards.length} of 30 posts` : `${toSchedule}/${total} posts ready${queuedSolo ? ` · ${queuedSolo} already queued` : ""}`}
              </p>
              <p className="text-xs text-muted-foreground truncate">{readyGraphics}/{realCards.length} graphics ready</p>
            </div>
            {scheduled > 0 ? (
              <Button onClick={() => navigate("/calendar")} data-testid="campaign-view-calendar" className="bg-primary hover:bg-primary/90 text-white shrink-0">
                <CalendarCheck className="h-4 w-4 mr-2" /> View calendar
              </Button>
            ) : isPreview ? (
              <Button onClick={() => navigate("/billing")} data-testid="campaign-upgrade-unlock-btn" className="bg-amber-500 hover:bg-amber-500/90 text-white shrink-0">
                <Lock className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Upgrade to unlock all 30 &amp; schedule</span><span className="sm:hidden">Unlock all 30</span>
              </Button>
            ) : (
              <div className="flex items-center gap-2 shrink-0">
                <Button onClick={shareWithClient} disabled={sharing} variant="outline" data-testid="campaign-share-btn"
                  className="border-border shrink-0">
                  {sharing ? <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" /> : <Share2 className="h-4 w-4 sm:mr-2" />}
                  <span className="hidden sm:inline">Share with client</span>
                </Button>
                <Button onClick={approve} disabled={approving || !canApprove} data-testid="campaign-approve-btn"
                  className={`shrink-0 text-white ${clientApproved ? "bg-emerald-600 hover:bg-emerald-600/90 ring-2 ring-emerald-400/50" : "bg-primary hover:bg-primary/90"}`}>
                  {approving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scheduling…</>
                    : <><CalendarCheck className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">{clientApproved ? "Schedule the approved month" : "Approve & Schedule Entire Month"}</span><span className="sm:hidden">Schedule</span></>}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* First-campaign celebration */}
      <Dialog open={celebrate !== null} onOpenChange={(o) => !o && setCelebrate(null)}>
        <DialogContent className="sm:max-w-md text-center overflow-hidden border-primary/40" data-testid="first-campaign-celebration">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} className="absolute top-0 block h-2 w-2 rounded-sm animate-confetti"
                style={{ left: `${(i * 6.5) % 100}%`,
                         backgroundColor: ["#6366F1", "#10B981", "#F59E0B", "#EC4899", "#38BDF8"][i % 5],
                         animationDelay: `${(i % 8) * 0.12}s` }} />
            ))}
          </div>
          <div className="relative pt-4 pb-2">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center mb-4">
              <CalendarCheck className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight">Your first month is live! 🎉</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              {celebrate} posts are scheduled to auto-publish at Facebook &amp; LinkedIn peak times. Sit back — AutoGridSocial takes it from here.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <Button data-testid="celebration-view-calendar" onClick={() => { setCelebrate(null); navigate("/calendar"); }}
                className="flex-1 bg-primary hover:bg-primary/90 text-white">
                <CalendarCheck className="h-4 w-4 mr-2" /> See my calendar
              </Button>
              <Button data-testid="celebration-dismiss" variant="outline" onClick={() => setCelebrate(null)} className="flex-1 border-border">
                Keep editing
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
